import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import BaseButton from "components/disw/BaseButton";
import { useState } from "react";
import {
  useRepublishDataSourceVersion,
  useUnpublishDataSourceVersion
} from "./hooks/useDataSources";

interface VersionPublishingOptions {
  solutionKey: string;
  dataSourceKey: string;
  version: string;
  isLive: boolean;
  onChanged: () => Promise<unknown>;
}

/** Unpublishes a live or republishes an unpublished version after confirmation via the returned dialog. */
export function useDataSourceVersionPublishing({
  solutionKey,
  dataSourceKey,
  version,
  isLive,
  onChanged
}: VersionPublishingOptions) {
  const [confirming, setConfirming] = useState(false);
  const [unpublish, unpublishState] = useUnpublishDataSourceVersion();
  const [republish, republishState] = useRepublishDataSourceVersion();
  const variables = { solutionKey, dataSourceKey, version };
  const action = isLive ? "Unpublish" : "Republish";

  const run = async () => {
    setConfirming(false);
    unpublishState.reset();
    republishState.reset();
    try {
      await (isLive ? unpublish({ variables }) : republish({ variables }));
      await onChanged();
    } catch {
      // the error is rendered from the mutation result
    }
  };

  const dialog = (
    <Dialog open={confirming} onClose={() => setConfirming(false)}>
      <DialogTitle>
        {action} version {version}?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isLive
            ? "API consumers will no longer be able to access this version. You can republish it within 90 days."
            : "API consumers will be able to access this version again. This will fail if the version was unpublished more than 90 days ago."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <BaseButton
          text="Cancel"
          styleType="default"
          onClick={() => setConfirming(false)}
        />
        <BaseButton
          text={action}
          styleType={isLive ? "caution" : "base"}
          onClick={run}
          data-testid={`data-source-version-${action.toLowerCase()}-confirm`}
        />
      </DialogActions>
    </Dialog>
  );

  return {
    action,
    requestChange: () => setConfirming(true),
    busy: unpublishState.loading || republishState.loading,
    error: unpublishState.error ?? republishState.error,
    dialog
  };
}
